/*
  Column definitions and shared rendering for the BGP incident log, which shows a different set of
  columns to every other alarm table: alarmsTableRenderers spreads bgpAlarmColumns into its column
  map and getCollectionConfig selects them when bgp is set.
*/
import React from 'react';
import { Box, ButtonLink, Flex, FlexColumn, Grid, Text, Tooltip } from 'core/components';
import { dateTimeFormatter } from 'core/util/dateUtils';
import { CONDITION_TYPE_LABELS, flaggedAsnRoleText } from '@kentik/ui-shared/synthetics/bgp';
import { alarmElapsed, formatAlarmElapsed } from 'app/views/synthetics/utils/alarmElapsed';
import { legacyFinding, legacyFlaggedAsns } from 'app/views/synthetics/utils/bgpLegacy';
import ASNRenderer from 'app/components/asn/ASNRenderer';

// the alarm condition, which the alarm key carries as a subtest
export function bgpSubtestLabel(subtest) {
  return subtest ? CONDITION_TYPE_LABELS[subtest] || 'Unknown' : null;
}

// ktrac's one sentence describing what the alarm found
function summaryText(bgp) {
  return bgp.summary || legacyFinding(bgp);
}

/*
  The BGP fields of a test error popover, used where the incident log shows a shared column set
  and so has no room for BGP columns of its own.
*/
export function bgpAlarmPopoverContent({ details, subtest }) {
  const bgp = details?.bgp || {};

  return (
    <Grid gridTemplateColumns="55px 1fr" gridGap={1} maxWidth={420}>
      <Text fontWeight="bold">Type:</Text>
      <Text>{bgpSubtestLabel(subtest) || bgp.display || '-'}</Text>
      <Text fontWeight="bold">Prefix:</Text>
      <Text>{bgp.prefix || '-'}</Text>
      <Text fontWeight="bold">Summary:</Text>
      <Text>{summaryText(bgp) || '-'}</Text>
    </Grid>
  );
}

/*
  One line per cause, which is the unit the evidence holds: an ASN two verdicts named reads twice,
  under each role. A role can name a second ASN of its own ('misplaced upstream, above AS3356').
*/
function flaggedAsnTooltip(rows, truncated) {
  return (
    <FlexColumn gap="2px" maxWidth={360}>
      {rows.map((row) => {
        const role = flaggedAsnRoleText(row);

        return (
          <Text key={`${row.asn}-${row.roleLabel}-${row.roleQualifier}-${row.vpCount}`} as="div">
            AS{row.asn}
            {role && ` — ${role}`}
            {Number.isFinite(row.vpCount) && ` (${row.vpCount} ${row.vpCount === 1 ? 'VP' : 'VPs'})`}
          </Text>
        );
      })}
      {truncated && (
        <Text as="div" muted small>
          Additional flagged ASNs may have been omitted.
        </Text>
      )}
    </FlexColumn>
  );
}

function elapsedRenderer({ model }) {
  const elapsed = alarmElapsed({
    startTime: model.get('start_time'),
    endTime: model.get('end_time'),
    state: model.get('state')
  });
  const formatted = formatAlarmElapsed(elapsed);

  if (!formatted) {
    return <Text muted>-</Text>;
  }

  // an active alarm is still accruing duration, so the value is only good as of page load
  const duration = (
    <Flex alignItems="baseline" gap="4px">
      <Text>{formatted}</Text>
      {elapsed.isActive && (
        <Text muted small>
          (ongoing)
        </Text>
      )}
    </Flex>
  );

  if (!elapsed.end) {
    return duration;
  }

  return <Tooltip content={`Ended ${dateTimeFormatter({ value: elapsed.end })}`}>{duration}</Tooltip>;
}

// setPrefixes filters the page to a prefix, and is absent where the table has nothing to filter
export function bgpAlarmColumns({ setPrefixes }) {
  return {
    bgpDisplay: {
      label: 'Type',
      name: 'details.bgp.display',
      width: 150,
      renderer: ({ model, value }) => {
        const label = bgpSubtestLabel(model.get('key.value.subtest'));

        if (label) {
          return label;
        }

        return (
          <Box>
            <Text as="div">{value}</Text>
          </Box>
        );
      }
    },

    bgpPrefix: {
      label: 'Prefix',
      name: 'details.bgp.prefix',
      width: 200,
      renderer: ({ value }) => {
        if (!value || !setPrefixes) {
          return '-';
        }

        return (
          <Box>
            <ButtonLink
              intent="primary"
              onClick={(e) => {
                // keep the row click (alarm details drawer) from firing when filtering by prefix
                e.stopPropagation();
                setPrefixes(value);
              }}
              minimal
              small
            >
              {value}
            </ButtonLink>
          </Box>
        );
      }
    },

    bgpFlaggedAsn: {
      label: 'Flagged ASNs',
      // topFlaggedAsn is the ranked head of flaggedAsns, carried as a scalar to sort and group on
      name: 'details.bgp.topFlaggedAsn',
      width: 220,
      renderer: ({ model }) => {
        const bgp = model.get('details.bgp') || {};
        const rows = bgp.flaggedAsns?.length ? bgp.flaggedAsns : legacyFlaggedAsns(bgp);

        if (rows.length === 0) {
          return <Text muted>-</Text>;
        }

        /*
          Rows are one per cause, so an ASN holding two roles owns two of them. The count is of
          distinct ASNs, and insertion order keeps the head the same ASN topFlaggedAsn carries.
        */
        const [top, ...rest] = [...new Set(rows.map((row) => row.asn))];

        return (
          <Tooltip content={flaggedAsnTooltip(rows, bgp.flaggedAsnsTruncated)}>
            <Flex alignItems="center" gap="4px">
              {/* the name renders as plain ellipsised text, since AsnLink has no width to give up */}
              <ASNRenderer asn={top} showLinks maxWidth="100%" />
              {rest.length > 0 && (
                <Text muted small>
                  +{rest.length}
                </Text>
              )}
            </Flex>
          </Tooltip>
        );
      }
    },

    bgpSummary: {
      label: 'Summary',
      // IncidentLogCollection sorts this column by VP impact, since the sentence sorts as nonsense
      name: 'details.bgp.summary',
      wrapText: true,
      /*
        Cell wraps a value in <Text ellipsis> unless ellipsis is false, and nowrap text gives the
        flex column a min-content width of the whole sentence, which widens the table instead of
        wrapping. minWidth keeps the remaining space from collapsing to the longest word.
      */
      ellipsis: false,
      minWidth: 260,
      renderer: ({ model }) => <Text>{summaryText(model.get('details.bgp') || {}) || '-'}</Text>
    },

    bgpElapsed: {
      label: 'Duration',
      name: 'elapsed',
      width: 150,
      renderer: elapsedRenderer
    }
  };
}

export const bgpGroupByOptions = [
  { label: 'Type', value: 'key.value.subtest' },
  { label: 'Prefix', value: 'details.bgp.prefix' },
  { label: 'Flagged ASN', value: 'details.bgp.topFlaggedAsn' }
];
